import {  useState, useEffect } from "react"
import type { ReactNode } from "react"
import { useAuth } from "../contexts/auth-context"
import { Button } from "../components/ui/button"
import { Avatar, AvatarFallback } from "../components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu"
import {
  Home,
  FileText,
  BarChart3,
  LogOut,
  Menu,
  ChevronLeft,
  ChevronRight,
  X,
  Sun,
  Moon,
  MemoryStickIcon,
  Headset,
  Search,
  KeyRound,
  type LucideIcon,
} from "lucide-react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { cn } from "../lib/utils"
import { motion, AnimatePresence } from "framer-motion"
import { useTheme } from "../contexts/theme-provider"


interface DashboardLayoutProps {
  children: ReactNode
}

type SidebarItem = { title: string; href: string; icon: LucideIcon; beta?: boolean }

// Base sidebar items shown to regular users.
const baseSidebarItems: SidebarItem[] = [
  { title: "Home", href: "/user/dashboard", icon: Home },
  { title: "Reports", href: "/user/reports", icon: FileText },
  { title: "Banks - Peer Analysis", href: "/user/peer-group-analysis", icon: FileText },
  { title: "Country Memo", href: "/user/country-memo", icon: MemoryStickIcon },
  { title: "Remaining Quota", href: "/user/quota", icon: BarChart3 },
  { title: "Support", href: "/user/support", icon: Headset },
]

// The Policy Search tab — only shown when the user has permission. Sits
// directly above Support and carries a Beta tag.
const policySearchItem: SidebarItem = { title: "Policy Search", href: "/user/policy-search", icon: Search, beta: true }

const logo = {
  backgroundImage: "url('/logo/logo.png')",
  backgroundSize: '200%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  height: '100vh',
  fontSize: '50px'
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const { theme, toggleTheme } = useTheme()

  // Build the sidebar items based on the user's role and permissions.
  // - PolicyUser: only the Policy Search tab.
  // - Admin/User with canAccessPolicySearch: the regular tabs + Policy Search.
  // - Everyone else: the regular tabs only.
  const isPolicyOnlyUser = user?.role === "PolicyUser"
  const canAccessPolicySearch = !!user?.canAccessPolicySearch
  const supportIndex = baseSidebarItems.findIndex(i => i.title === "Support")
  const sidebarItems = isPolicyOnlyUser
    ? [policySearchItem]
    : canAccessPolicySearch
    ? [...baseSidebarItems.slice(0, supportIndex), policySearchItem, ...baseSidebarItems.slice(supportIndex)]
    : baseSidebarItems

  // PolicyUser should only ever be on the policy-search page.
  // If they land anywhere else, redirect them.
  useEffect(() => {
    if (isPolicyOnlyUser && location.pathname !== "/user/policy-search") {
      navigate("/user/policy-search", { replace: true })
    }
  }, [isPolicyOnlyUser, location.pathname, navigate])
  
  // Check if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
      if (window.innerWidth < 768) {
        setSidebarOpen(false)
      }
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    
    return () => {
      window.removeEventListener('resize', checkIsMobile)
    }
  }, [])

  const handleLogout = () => {
    logout()
    navigate("/")
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  return (
    <div
      className={`!min-h-screen !flex !transition-all !duration-500 !bg-gradient-to-br 
      ${theme === "dark"? "!from-slate-900 !via-slate-800 !to-slate-900" : "!from-slate-50 !via-white !to-blue-50"}`}
    >
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="!fixed !inset-0 !bg-black/50 !z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <motion.aside
        initial={{ x: isMobile ? -300 : 0, opacity: 0 }}
        animate={{ 
          x: isMobile ? (sidebarOpen ? 0 : -300) : 0, 
          opacity: 1 
        }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={cn(
          `!backdrop-blur-xl !shadow-2xl !transition-all !duration-300 !ease-in-out !flex !flex-col !border-r !relative !z-30 !bg-white/80 !shadow-black/5 !border-slate-200/50 
          ${theme === "dark"? "!bg-slate-800/80 !shadow-black/20 !border-slate-700/50" : ""}`,
          sidebarOpen ? "!w-72" : "!w-20",
          isMobile && "!fixed !left-0 !top-0 !bottom-0"
        )}
      >
        {/* Sidebar header */}
        <div className={`!flex !items-center !justify-between !h-16 md:!h-20 !px-3 md:!px-4 !border-b ${theme === "dark" ? "!border-slate-700/50" : "!border-slate-200/50"}`}>
          <AnimatePresence mode="wait">
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="!flex !items-center !space-x-2 md:!space-x-3"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                  className="cursor-pointer !w-8 !h-8 md:!w-10 md:!h-10 !flex !items-center !justify-center !bg-transparent"
                  onClick={() => navigate("/user/dashboard")} 
                >
                  <div style={logo}
                    className="!w-full !h-full !object-contain" 
                  />
                </motion.div>
                <div>
                  <span className={`cursor-pointer !text-lg md:!text-xl !font-black !bg-gradient-to-r !from-slate-900 !to-blue-900 ${theme === "dark" ? "!from-white !to-blue-100" : ""} !bg-clip-text !text-transparent`}
                  onClick={()=> navigate("/user/dashboard")}>
                    CredexAI
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className={`!p-1 md:!p-2 cursor-pointer !rounded-lg md:!rounded-xl !bg-slate-100/50 ${theme === "dark" ? "!bg-slate-700/50" : ""} hover:!bg-slate-200/50 ${theme === "dark" ? "hover:!bg-slate-600/50" : ""} !transition-all !duration-200`}
            >
              {isMobile ? (
                <X className="!w-4 !h-4 md:!w-5 md:!h-5" />
              ) : (
                <motion.div animate={{ rotate: sidebarOpen ? 0 : 180 }} transition={{ duration: 0.3 }}>
                  {sidebarOpen ? <ChevronLeft className="!w-4 !h-4 md:!w-5 md:!h-5" /> : <ChevronRight className="!w-4 !h-4 md:!w-5 md:!h-5" />}
                </motion.div>
              )}
            </Button>
          </motion.div>
        </div>

        {/* Navigation */}
        <nav className="!flex-1 !px-2 md:!px-3 !py-4 md:!py-6 !space-y-1 md:!space-y-2">
          {sidebarItems.map((item, index) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href
            return (
              <motion.div
                key={item.href}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
              >
                <Link to={item.href} onClick={() => isMobile && setSidebarOpen(false)}>
                  <motion.div
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    className={cn(
                      "!flex !items-center !rounded-lg md:!rounded-xl !text-sm !font-semibold !transition-all !duration-200 !px-3 !py-2 md:!px-4 md:!py-3 !relative !group",
                      isActive
                        ? "!bg-gradient-to-r !from-blue-500 !to-purple-600 !text-white !shadow-lg !shadow-blue-500/25"
                        : `!text-slate-600 dark:!text-slate-400 ${theme === "dark" ? "!text-slate-300 hover:!bg-slate-700/50 hover:!text-white" : "hover:!bg-slate-100/70 hover:!text-slate-900 dark:hover:!text-white"}`
                    )}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="!absolute !inset-0 !bg-gradient-to-r !from-blue-500 !to-purple-600 !rounded-lg md:!rounded-xl"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <Icon className={cn("!h-4 !w-4 md:!h-5 md:!w-5 !flex-shrink-0 !relative !z-10", isActive && "!text-white")} />
                    <AnimatePresence mode="wait">
                      {sidebarOpen && (
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                          className={cn("!ml-2 md:!ml-3 !relative !z-10 !text-xs md:!text-sm", isActive && "!text-white")}
                        >
                          {item.title}
                          {item.beta && (
                            <span className="!ml-1.5 !inline-block !px-1.5 !py-px !rounded-full !text-[9px] !font-bold !uppercase !tracking-wide !bg-amber-100 !text-amber-700 dark:!bg-amber-900/60 dark:!text-amber-300 !align-middle">
                              Beta
                            </span>
                          )}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </Link>
              </motion.div>
            )
          })}
        </nav>

        {/* Theme Toggle */}
        <div className="!px-2 md:!px-3 !pb-2 md:!pb-3">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              variant="ghost"
              onClick={toggleTheme}
              className={cn(
                `!w-full !justify-start !rounded-lg md:!rounded-xl !text-xs md:!text-sm !font-semibold !transition-all !duration-200 !px-3 !py-2 md:!px-4 md:!py-3 
                ${theme === "dark"? "!text-slate-300 hover:!bg-slate-700/50 hover:!text-white" : "!text-slate-600 dark:!text-slate-400 hover:!bg-slate-100/70 hover:!text-slate-900 dark:hover:!text-white"}`
              )}

            >
              <motion.div animate={{ rotate: theme === "dark" ? 180 : 0 }} transition={{ duration: 0.3 }}>
              {theme === "dark" ? <Sun className="!h-4 !w-4 md:!h-5 md:!w-5" /> : <Moon className="!h-4 !w-4 md:!h-5 md:!w-5" />}
            </motion.div>

            <AnimatePresence mode="wait">
              {sidebarOpen && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="!ml-2 cursor-pointer md:!ml-3 !text-xs md:!text-sm"
                >
                  {theme === "dark" ? "Light Mode" : "Dark Mode"}
                </motion.span>
              )}
            </AnimatePresence>

            </Button>
          </motion.div>
        </div>

        {/* User info */}
        <div className={`!p-3 md:!p-4 !border-t ${theme === "dark" ? "!border-slate-700/50" : "!border-slate-200/50"}`}>
          <motion.div
            whileHover={{ scale: 1.02 }}
            className={`!flex !items-center !space-x-2 md:!space-x-3 !p-2 md:!p-3 !rounded-lg md:!rounded-xl 
              ${theme === "dark" ? "!bg-slate-700/30" : "!bg-slate-50/50"} !transition-all !duration-200`}
          >
            <motion.div whileHover={{ scale: 1.1 }}>
              <Avatar className="!h-8 !w-8 md:!h-10 md:!w-10 !ring-2 !ring-blue-500/20">
                <AvatarFallback className="!bg-gradient-to-br !from-blue-500 !to-purple-600 !text-white !font-bold !text-xs md:!text-sm">
                  {user?.username?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </motion.div>
            <AnimatePresence mode="wait">
              {sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="!flex-1 !min-w-0"
                >
                  <p className={`!text-xs md:!text-sm !font-semibold ${theme === "dark" ? "!text-white" : "!text-slate-900 dark:!text-white"} !truncate`}>{user?.username}</p>
                  <p className={`!text-xs ${theme === "dark" ? "dark:!text-slate-400" : "!text-slate-500"} !truncate`}>{user?.username}</p>

                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </motion.aside>

      {/* Main content */}
      <div className="!flex-1 !flex !flex-col !min-w-0">
        {/* Top bar */}
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className= {theme === "dark" ? "!sticky !top-0 !z-20 dark:!bg-slate-800/70 !backdrop-blur-xl !shadow-lg dark:!shadow-black/20 dark:!border-slate-700/50" : "!sticky !top-0 !z-20 !bg-white/70  !shadow-black  !border-b !border-slate-200/50 "}
        >
          <div className="!flex !items-center !justify-between !h-16 md:!h-14 !px-4 md:!px-6 lg:!px-8">
            {/* Mobile sidebar toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:!hidden !p-2 !rounded-xl"
              onClick={toggleSidebar}
            >
              <Menu className="!h-5 !w-5" />
            </Button>

            <div className="!flex !items-center !space-x-3 md:!space-x-4 !ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="ghost"
                      className="!relative cursor-pointer !h-8 !w-8 md:!h-10 md:!w-10 !rounded-full !ring-2 !ring-blue-500/20 hover:!ring-blue-500/40 !transition-all !duration-200"
                    >
                      <Avatar className="!h-8 !w-8 md:!h-10 md:!w-10">
                        <AvatarFallback className="!bg-gradient-to-br !from-blue-500 !to-purple-600 !text-white !font-bold !text-xs md:!text-sm">
                          {user?.username?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </motion.div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="!w-56 md:!w-64 !bg-white/90 dark:!bg-slate-800/90 !backdrop-blur-xl !border-slate-200/50 dark:!border-slate-700/50"
                  align="end"
                  forceMount
                >
                  <DropdownMenuLabel className="!font-normal !p-3 md:!p-4">
                    <div className="!flex !flex-col !space-y-1 md:!space-y-2">
                      <p className="!text-xs md:!text-sm !font-medium !text-slate-900 dark:!text-white">{user?.username}</p>
                      <p className="!text-xs !leading-none !text-slate-500 dark:!text-slate-400">{user?.username}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="!bg-slate-200/50 dark:!bg-slate-700/50" />
                  <DropdownMenuItem
                    onClick={() => navigate("/user/change-password")}
                    className="!p-2 md:!p-3 !text-slate-700 dark:!text-slate-200 hover:!bg-slate-100 dark:hover:!bg-slate-700/50 !cursor-pointer !text-xs md:!text-sm"
                  >
                    <KeyRound className="!mr-2 !h-4 !w-4" />
                    <span className="!font-medium">Change Password</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="!p-2 md:!p-3 !text-red-600 dark:!text-red-400 hover:!bg-red-50 dark:hover:!bg-red-900/20 !cursor-pointer !text-xs md:!text-sm"
                  >
                    <LogOut className="!mr-2 !h-4 !w-4" />
                    <span className="!font-medium">Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </motion.header>

        {/* Page content */}
        <main className="!flex-1">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="!w-full !h-full"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  )
}